/* Archived document history: the finished side of the Documents page, plus its CSV export.
   Hand-typed until the records lane's schema.d.ts regenerates these (m4-rulings.md #59). */

import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { apiBlob, apiGet } from "./client";
import type { DocumentFilters, DocumentSummary, Page } from "./types";

/** `DocumentRow` plus the six history fields, so one row renderer serves both lists. */
export type HistoryRow = DocumentSummary & {
  participantFrom: string | null;
  participantTo: string | null;
  net: number | null;
  vat: number | null;
  total: number | null;
  archivedAt: string | null;
};

const PAGE_SIZE = 50;

/** Filters only. `limit` and `cursor` are pagination, and an export must not be paginated.
    `family` and `typeCode` are here so an exported CSV matches the rows on screen: the
    queue serves some searches from /api/history, and a filter the server dropped would
    read as applied while the export quietly carried everything. */
const FILTER_KEYS = [
  "direction", "state", "q", "entityId", "from", "to", "family", "typeCode",
] as const;

export function historyQuery(
  filters: DocumentFilters,
  page?: { limit?: number; cursor?: string | null },
): string {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  if (page?.limit) params.set("limit", String(page.limit));
  if (page?.cursor) params.set("cursor", page.cursor);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function useHistory(filters: DocumentFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: ["history", "list", filters],
    queryFn: ({ pageParam }) =>
      apiGet<Page<HistoryRow>>(
        `/api/history${historyQuery(filters, { limit: PAGE_SIZE, cursor: pageParam })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

export function historyFilename(filters: DocumentFilters): string {
  return `hub-history-${filters.from || "all"}-${filters.to || "all"}.csv`;
}

/** The CSV is fetched as a blob on click; the caller builds and revokes the object URL. */
export function useHistoryExport() {
  return useMutation({
    mutationFn: (filters: DocumentFilters) => apiBlob(`/api/history/export.csv${historyQuery(filters)}`),
  });
}
